import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@core/contexts/AuthContext'
import { useAuthConfig, googleHabilitado } from '@core/hooks/useAuthConfig'
import { LARGO_MINIMO_PASSWORD, errorPasswordNueva } from '@core/lib/accionAuth'

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
)

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/>
  </svg>
)
const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-8-10-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
)

export default function LoginPage() {
  // El destino al que volver tras el login (Paso 4, `state.volverA`) viaja
  // también al registro: quien llega desde el link de una entrada regalada y
  // todavía no tiene cuenta no puede perder el link en el camino (Paso 6).
  const location = useLocation()
  const { login, loginEmail, registerWithInvite, registerWithInviteGoogle, resetPassword, faltaVerificar, reenviarVerificacion, error, setError, mfaResolver, submitMfaCode, cancelMfa } = useAuth()
  const authCfg = useAuthConfig()  // configuracion/auth — qué métodos de login se ofrecen

  // Modos: 'main' (login normal) | 'invite' (canje de código)
  const [mode, setMode] = useState('main')
  const [busy, setBusy] = useState(false)
  // Tras autenticar OK, onAuthStateChanged sigue resolviendo userDoc + rol +
  // asignaciones (unos segundos). Sin feedback, la pantalla queda igual al login
  // y el usuario cree que falló y reintenta. `entrando` muestra un overlay
  // "Entrando…" hasta que la app termina de cargar y navega sola.
  const [entrando, setEntrando] = useState(false)
  const [inviteMethod, setInviteMethod] = useState('google')

  // SEC-F0.3 — prompt MFA cuando el usuario tiene 2FA enrollado.
  const [mfaCode, setMfaCode] = useState('')
  const [mfaBusy, setMfaBusy] = useState(false)

  async function handleMfaSubmit(e) {
    e.preventDefault()
    if (!mfaResolver) return
    if (!/^\d{6}$/.test(mfaCode.trim())) {
      setError?.('Ingresá los 6 dígitos del código')
      return
    }
    setMfaBusy(true)
    try {
      await submitMfaCode(mfaCode.trim())
      setMfaCode('')
    } catch (err) {
      console.error('[Login] mfa submit:', err)
      // El error ya quedó en el state error vía submitMfaCode
    } finally {
      setMfaBusy(false)
    }
  }

  function handleMfaCancel() {
    cancelMfa()
    setMfaCode('')
  }

  // Form state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPass, setShowPass] = useState(false)
  const [nombre, setNombre] = useState('')
  const [codigo, setCodigo] = useState('')
  const [info, setInfo] = useState('')
  const [reenviando, setReenviando] = useState(false)

  // Si el flujo de auth termina en error (ej. onAuthStateChanged rebota sin
  // userDoc y hace signOut), liberamos "entrando" + busy para que el usuario
  // pueda reintentar y no quede el botón deshabilitado.
  useEffect(() => {
    if (error) { setEntrando(false); setBusy(false) }
  }, [error])

  async function handleGoogle() {
    setBusy(true); setInfo('')
    try {
      await login(authCfg)
      // Éxito: dejamos el overlay "Entrando…" hasta que la app navegue. NO
      // reseteamos busy/entrando acá porque onAuthStateChanged sigue cargando.
      setEntrando(true)
    }
    catch (e) {
      if (e?.code !== 'auth/popup-closed-by-user') {
        console.error('[Login] Google:', e)
      }
      setBusy(false)
    }
  }

  async function handleLoginEmail(e) {
    e.preventDefault()
    setBusy(true); setInfo('')
    try {
      await loginEmail(email, password)
      setEntrando(true)  // overlay "Entrando…" hasta que la app navegue
    }
    catch (e) {
      console.error('[Login] email+pass:', e)
      if (!error) setError?.(e?.message || 'No se pudo iniciar sesión')
      setBusy(false)
    }
  }

  async function handleInvite(e) {
    e.preventDefault()
    const errorPass = errorPasswordNueva(password)
    if (errorPass) { setError?.(errorPass); return }
    setBusy(true); setInfo('')
    try {
      await registerWithInvite({ codigo, email, password, nombre })
      // PR 3b: el canje lo hace el servidor y exige el email verificado — se
      // completa en el primer inicio de sesión después de verificar.
      setInfo('Te enviamos un email para verificar tu cuenta. Cuando lo confirmes, iniciá sesión y tu invitación se activará sola.')
    } catch (err) {
      console.error('[Login] registerWithInvite:', err)
      const msg = err?.code === 'auth/email-already-in-use'
        ? 'Ya existe una cuenta con ese email. Si es tuya, iniciá sesión.'
        : err?.code === 'auth/weak-password'
        ? `La contraseña es muy débil (mínimo ${LARGO_MINIMO_PASSWORD} caracteres).`
        : err?.code === 'auth/invalid-email'
        ? 'El email no es válido.'
        : err?.message || 'No se pudo activar la cuenta'
      if (!error) setError?.(msg)
    } finally { setBusy(false) }
  }

  async function handleInviteGoogle() {
    if (!codigo.trim()) {
      setError?.('Ingresá tu código de invitación')
      return
    }
    setBusy(true); setInfo('')
    try {
      await registerWithInviteGoogle({ codigo })
    } catch (err) {
      console.error('[Login] registerWithInviteGoogle:', err)
      if (err?.code === 'auth/popup-closed-by-user') {
        // user cerró el popup, no mostramos error
      } else if (!error) {
        setError?.(err?.message || 'No se pudo activar la cuenta con Google')
      }
    } finally { setBusy(false) }
  }

  async function handleReset() {
    if (!email) { setError?.('Ingresá tu email para recuperar la contraseña'); return }
    setBusy(true); setInfo('')
    try {
      await resetPassword(email)
      // Anti-enumeración (A3): el mismo texto exista o no la cuenta.
      setInfo('Si hay una cuenta con ese email, te mandamos un correo para elegir una contraseña nueva.')
    } catch {} finally { setBusy(false) }
  }

  // A3 — el candado de verificación dejó afuera: reenviar el mail con la
  // contraseña del form (la sesión para mandarlo dura sólo el envío).
  async function handleReenviarVerificacion() {
    if (!email || !password) { setError?.('Ingresá tu email y tu contraseña para reenviar el mail de verificación.'); return }
    setReenviando(true); setInfo('')
    try {
      const r = await reenviarVerificacion(email, password, location.state?.volverA ?? null)
      if (r?.enviado || r?.motivo === 'ya-verificado') setInfo(r.mensaje)
      else setError?.(r?.mensaje || 'No se pudo reenviar el mail.')
    } finally { setReenviando(false) }
  }

  function switchMode(m) {
    setMode(m); setInfo(''); setError?.(null)
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-auto py-10">
      <div className="bg-glow w-[500px] h-[500px] bg-brand-accent -top-[150px] -right-[100px]" />
      <div className="bg-glow w-[400px] h-[400px] bg-brand-purple -bottom-[100px] -left-[100px]" />

      {/* Overlay "Entrando…" — se muestra tras autenticar OK mientras la app
          resuelve userDoc + rol + asignaciones y navega. Evita que la pantalla
          parezca el login y el usuario reintente. */}
      {entrando && !error && (
        <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm animate-fade-in">
          <div className="w-10 h-10 border-[3px] border-white/15 border-t-brand-accent rounded-full animate-spin" />
          <p className="mt-5 text-[15px] font-semibold text-white">Entrando…</p>
          <p className="mt-1 text-[13px] text-zinc-400">Preparando tu espacio de trabajo</p>
        </div>
      )}

      {/* SEC-F0.3 — Prompt MFA cuando el usuario tiene 2FA enrollado.
          Se renderiza encima del login normal mientras resolverSignIn está pendiente. */}
      {mfaResolver && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="glass-card p-8 w-full max-w-[420px] mx-5 shadow-2xl animate-scale-in">
            <h2 className="text-[20px] font-extrabold text-white mb-1">Código de verificación</h2>
            <p className="text-[13px] text-zinc-400 mb-5">
              Tenés 2FA activado. Abrí tu app de autenticación y pegá el código actual de 6 dígitos.
            </p>
            <form onSubmit={handleMfaSubmit} className="space-y-3">
              <input
                type="text"
                inputMode="numeric"
                pattern="\d*"
                maxLength={6}
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                autoFocus
                className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-[20px] tracking-[0.4em] font-mono text-white text-center placeholder:text-zinc-600 focus:outline-none focus:border-brand-accent/50"
              />
              {error && (
                <div className="text-[12px] bg-rose-500/10 border border-rose-500/30 text-rose-200 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={mfaBusy || mfaCode.length !== 6}
                className="w-full py-3 px-6 bg-emerald-500 text-zinc-900 font-semibold rounded-xl hover:bg-emerald-400 transition-all disabled:opacity-40 text-[14px]"
              >
                {mfaBusy ? 'Verificando…' : 'Verificar y entrar'}
              </button>
              <button
                type="button"
                onClick={handleMfaCancel}
                disabled={mfaBusy}
                className="w-full py-2.5 px-6 bg-white/[0.04] border border-white/[0.08] text-zinc-300 font-medium rounded-xl hover:bg-white/[0.08] transition-all text-[13px]"
              >
                Cancelar
              </button>
              <div className="text-[11px] text-zinc-500 text-center pt-1">
                ¿Perdiste el celular? Pedile a un admin que limpie tu segundo factor.
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="glass-card p-10 w-full max-w-[460px] mx-5 shadow-2xl text-center animate-scale-in">
        <h1 className="text-[40px] font-extrabold tracking-tight leading-none mb-0.5">
          <span className="bg-gradient-to-br from-brand-accent to-brand-purple bg-clip-text text-transparent">
            Milbrands
          </span>{' '}
          CoWork
        </h1>
        <p className="text-zinc-500 text-[14px] mb-8">Nuestro entorno IA</p>

        {/* ── Login principal (sin tabs) ── */}
        {mode === 'main' && (
          <div className="space-y-4 text-left">
            {/* Google — se ofrece según configuracion/auth (solo-milbrands/todos/off). */}
            {googleHabilitado(authCfg) && (
              <button
                onClick={handleGoogle}
                disabled={busy}
                className="flex items-center justify-center gap-3 w-full py-3.5 px-6 bg-white/[0.07] border border-white/[0.12] rounded-[14px] text-white font-medium text-[15px] transition-all duration-200 hover:bg-white/[0.12] hover:border-white/20 hover:-translate-y-px active:translate-y-0 disabled:opacity-50 disabled:cursor-wait"
              >
                {busy ? (
                  <>
                    <div className="w-5 h-5 border-2 border-transparent border-t-white rounded-full animate-spin" />
                    Conectando...
                  </>
                ) : (
                  <>
                    <GoogleIcon />
                    Continuar con Google
                  </>
                )}
              </button>
            )}

            {/* Divisor solo si se ofrecen AMBOS métodos. */}
            {googleHabilitado(authCfg) && authCfg.passwordEnabled && (
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-white/5" />
                <span className="text-[11px] uppercase tracking-widest text-zinc-600">o con email</span>
                <div className="flex-1 h-px bg-white/5" />
              </div>
            )}

            <form onSubmit={handleLoginEmail} className="space-y-3">
              <div>
                <label className="text-[12px] text-zinc-400 block mb-1.5 ml-1">Email</label>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-[14px] focus:outline-none focus:border-brand-accent/50"
                  placeholder="tu@empresa.com"
                />
              </div>
              <div>
                <label className="text-[12px] text-zinc-400 block mb-1.5 ml-1">Contraseña</label>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 pr-11 bg-white/[0.04] border border-white/[0.08] rounded-xl text-[14px] focus:outline-none focus:border-brand-accent/50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(s => !s)}
                    aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    {showPass ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full py-3 px-6 bg-white/[0.06] border border-white/[0.1] text-white font-medium rounded-xl hover:bg-white/[0.1] transition-all disabled:opacity-50 text-[14px]"
              >
                {busy ? 'Ingresando...' : 'Ingresar'}
              </button>
              <div className="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={() => switchMode('invite')}
                  className="text-[12px] text-zinc-400 hover:text-brand-accent"
                >
                  Tengo un código de invitación
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="text-[12px] text-zinc-400 hover:text-brand-accent"
                >
                  Olvidé mi contraseña
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ── Canje de código de invitación ── */}
        {mode === 'invite' && (
          <div className="space-y-4 text-left">
            <p className="text-[12px] text-zinc-500 text-center">
              Ingresá el código que recibiste por parte de Milbrands
            </p>

            <div>
              <label className="text-[12px] text-zinc-400 block mb-1.5 ml-1">Código de invitación</label>
              <input
                type="text"
                value={codigo}
                onChange={e => setCodigo(e.target.value.toUpperCase())}
                required
                maxLength={20}
                className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-[14px] focus:outline-none focus:border-brand-accent/50 font-mono tracking-widest text-center uppercase"
                placeholder="XXXX-XXXX"
              />
            </div>

            {inviteMethod === 'google' && (
              <>
                <button
                  type="button"
                  onClick={handleInviteGoogle}
                  disabled={busy}
                  className="flex items-center justify-center gap-3 w-full py-3.5 px-6 bg-brand-accent text-white font-semibold rounded-[14px] text-[15px] transition-all duration-200 hover:bg-brand-accent/90 hover:-translate-y-px active:translate-y-0 disabled:opacity-50 disabled:cursor-wait"
                >
                  {busy ? (
                    <>
                      <div className="w-5 h-5 border-2 border-transparent border-t-white rounded-full animate-spin" />
                      Activando...
                    </>
                  ) : (
                    <>
                      <GoogleIcon />
                      Activar con Google
                    </>
                  )}
                </button>
                <p className="text-[11px] text-zinc-500 text-center -mt-2">
                  Vas a usar tu cuenta de Google para entrar siempre
                </p>
                <button
                  type="button"
                  onClick={() => setInviteMethod('email')}
                  className="w-full text-[12px] text-zinc-500 hover:text-brand-accent pt-1"
                >
                  ¿No usás Google? Activar con email y contraseña
                </button>
              </>
            )}

            {inviteMethod === 'email' && (
              <form onSubmit={handleInvite} className="space-y-3">
                <div>
                  <label className="text-[12px] text-zinc-400 block mb-1.5 ml-1">Nombre</label>
                  <input
                    type="text"
                    value={nombre}
                    onChange={e => setNombre(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-[14px] focus:outline-none focus:border-brand-accent/50"
                    placeholder="Tu nombre"
                  />
                </div>
                <div>
                  <label className="text-[12px] text-zinc-400 block mb-1.5 ml-1">Email</label>
                  <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-[14px] focus:outline-none focus:border-brand-accent/50"
                    placeholder="tu@empresa.com"
                  />
                </div>
                <div>
                  <label className="text-[12px] text-zinc-400 block mb-1.5 ml-1">Contraseña</label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    minLength={LARGO_MINIMO_PASSWORD}
                    className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-[14px] focus:outline-none focus:border-brand-accent/50"
                    placeholder={`Mínimo ${LARGO_MINIMO_PASSWORD} caracteres`}
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full py-3.5 px-6 bg-brand-accent text-white font-semibold rounded-xl hover:bg-brand-accent/90 transition-all disabled:opacity-50"
                >
                  {busy ? 'Activando...' : 'Activar mi cuenta'}
                </button>
                <button
                  type="button"
                  onClick={() => setInviteMethod('google')}
                  className="w-full text-[12px] text-zinc-500 hover:text-brand-accent pt-1"
                >
                  ← Volver a activar con Google
                </button>
              </form>
            )}

            <div className="pt-2 border-t border-white/5">
              <button
                type="button"
                onClick={() => switchMode('main')}
                className="w-full text-[12px] text-zinc-500 hover:text-zinc-300"
              >
                ← Volver al inicio de sesión
              </button>
            </div>
          </div>
        )}

        {info && (
          <div className="mt-4 px-4 py-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-300 text-[13px] animate-fade-in">
            {info}
          </div>
        )}
        {error && (
          <div className="mt-4 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 text-[13px] animate-fade-in">
            {error}
          </div>
        )}
        {faltaVerificar && mode === 'main' && (
          <button
            type="button"
            onClick={handleReenviarVerificacion}
            disabled={reenviando}
            className="mt-2 text-[12px] text-zinc-400 hover:text-brand-accent disabled:opacity-50"
          >
            {reenviando ? 'Reenviando…' : 'Reenviar el mail de verificación'}
          </button>
        )}

        {/* Link a registro */}
        <div className="mt-6 pt-4 border-t border-white/[0.04] text-center">
          <Link to="/registro" state={location.state} className="text-[12px] text-zinc-500 hover:text-brand-accent transition-colors">
            No tengo cuenta — Registrarme
          </Link>
        </div>

        {/* Atribución reCAPTCHA (el badge está oculto por CSS — Google pide mostrar este texto). */}
        <p className="mt-4 text-center text-[10px] text-zinc-600 leading-relaxed">
          Protegido por reCAPTCHA — aplican la{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline hover:text-zinc-400">Política de Privacidad</a>{' '}y los{' '}
          <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline hover:text-zinc-400">Términos</a>{' '}de Google.
        </p>
      </div>
    </div>
  )
}
